import { Role } from './enums';

export const enum PaymentType {
  Unknown,
  ElektronicTransfer,
  PayDeduction,
  Card,
  Account,
  Cash,
  InvoicePostPayment = 6,
  InvoicePerOccasion = 7,
  InvoiceInternal = 8,
  ExternalInvoice = 9,
  InvoicePrePayment = 10,
  MobilePay = 11,
  Subscription = 12,
}

/** One payment form as it is set up — see `PAYMENT_TYPES`. */
export interface PaymentTypeSetting {
  id: PaymentType;

  /** What an administrator sees, here and in the booking dialog */
  name: string;

  /** Offered when a booking is paid for. The others only ever appear on existing bookings. */
  bookable?: boolean;

  /** Shown behind an info icon where there is room for it */
  description?: string;
}

/**
 * The payment forms as they are set up, in the order they should be listed.
 *
 * Mirrors the `paymenttype` table — the ids are its own, and the three marked "udgået" below are
 * its `IsDeleted` rows, kept so an old booking still shows what it was paid by. It lives in the
 * frontend because the API exposes no endpoint for the table; a screen that needs a payment
 * form's name or the list of them should read it from here rather than spell out ids of its own,
 * so that the day the API does expose them there is one place to change.
 */
export const PAYMENT_TYPES: PaymentTypeSetting[] = [
  { id: PaymentType.ElektronicTransfer, name: 'Giro' }, // udgået
  { id: PaymentType.PayDeduction, name: 'Løntræk' }, // udgået
  {
    id: PaymentType.Card,
    name: 'Betalingskort',
    bookable: true,
    description: 'Betalingsfristen må ikke ligge efter det bookede tidspunkt',
  },
  { id: PaymentType.Account, name: 'PSP' }, // udgået
  { id: PaymentType.Cash, name: 'Kontant', bookable: true },
  { id: PaymentType.InvoicePostPayment, name: 'Fakturering (KMD)', bookable: true },
  { id: PaymentType.InvoicePerOccasion, name: 'Fakturering pr. gang (KMD)' },
  { id: PaymentType.InvoiceInternal, name: 'Intern fakturering (KMD)' },
  { id: PaymentType.ExternalInvoice, name: 'Ekstern faktura (KMD)' },
  { id: PaymentType.InvoicePrePayment, name: 'Forudbetaling (KMD)' },
  { id: PaymentType.MobilePay, name: 'MobilePay' },
  { id: PaymentType.Subscription, name: 'Abonnement' },
];

/** The payment forms a booking can be paid by, in setup order. */
export const BOOKABLE_PAYMENT_TYPES: PaymentTypeSetting[] = PAYMENT_TYPES.filter((type) => type.bookable);

const NAMES_BY_ID = new Map(PAYMENT_TYPES.map((type) => [type.id as number, type.name]));

/**
 * What to call a payment form — the whole setup, not just the bookable ones, since an existing
 * booking can carry a form that is no longer offered.
 *
 * @param paymentTypeId Id as it is stored on the booking
 * @returns Its name, or "Ikke angivet" for a booking with no payment form set — the id is 0 on
 * anything that predates the field, which is most of the older bookings
 */
export function paymentTypeName(paymentTypeId: number | null | undefined): string {
  if (!paymentTypeId) {
    return 'Ikke angivet';
  }

  return NAMES_BY_ID.get(paymentTypeId) ?? `Ukendt (${paymentTypeId})`;
}

/**
 * The payment form a booking from the association flow (Søg/Book, Arrangement) carries
 * @remarks A company is invoiced (InvoicePostPayment) and an internal customer internally
 * (InvoiceInternal); an association gets the flow's own form — invoiced in Søg/Book as at the
 * counter, none (Unknown) for an event request. Never Kontant: the invoice routine books Kontant as
 * a cash payment of the full amount (#950). Until the setup per user type (#800).
 * @param organisationTypeId The type of the organisation booked for (15, 4 or 6)
 * @param fallback The flow's own form for an association
 * @returns The payment form to book with
 */
export function defaultPaymentTypeFor(
  organisationTypeId: number | null | undefined,
  fallback: PaymentType,
): PaymentType {
  switch (organisationTypeId) {
    case Role.Company:
      return PaymentType.InvoicePostPayment;
    case Role.InternalUser:
      return PaymentType.InvoiceInternal;
    default:
      return fallback;
  }
}
